/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Dropdown, Input, Option, makeStyles, tokens, Field, Skeleton, SkeletonItem, Spinner } from '@fluentui/react-components';
import { ArrowCircleLeftRegular, CheckmarkCircle16Regular } from '@fluentui/react-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAutomationRuleFormProvider } from '../form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAutomationRuleSettings } from '../settings/provider';
import AuthorizationStepsWithModal, { useAuthorizationStepsWithModalRef } from 'src/components/shared/authorization-steps/with-modal';
import { WithTooltip } from 'src/components/ui/with-tooltip';

const statusOptions = [
	{ value: 'enabled', label: 'Enabled' },
	{ value: 'disabled', label: 'Disabled' },
];

export default function AutomationRuleHeader() {
	const classes = useStyles();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const {
		saveAutomationRule,
		updateAutomationRule,
		tab,
		title,
		setTitle,
		status,
		setStatus,
		titleValidation,
		loadingPage,
		submitting,
	} = useAutomationRuleFormProvider();
	const { spHasAccess, userHasAccess } = useAutomationRuleSettings();

	const [titleMode, setTitleMode] = useState<'title' | 'input'>('input');

	const authorizationStepsWithModalRef = useAuthorizationStepsWithModalRef();

	const showAuthMessageBar = useMemo(() => {
		return !spHasAccess && !userHasAccess;
	}, [userHasAccess, spHasAccess]);
	
	const onSave = useCallback(() => {
		const id = searchParams.get('id');
		if (id) {
			updateAutomationRule(id);
		}
		if (!id) {
			saveAutomationRule();
		}
	}, [
		searchParams,
		updateAutomationRule,
		saveAutomationRule,
	]);

	useEffect(() => {
		if (tab === 'rule' && searchParams.get('id') !== '') {
			setTitleMode('title');
		}

		if (tab === 'rule' && ['', undefined, null].includes(searchParams.get('id'))) {
			setTitleMode('input');
		}
	}, [tab]);
  
	return (
		<div className={classes.root}>
			<div className={classes.header}>
				<div className={classes.headerWrapper}>
					<div className={classes.headerContentLeft}>
						{loadingPage && (
							<Skeleton>
								<SkeletonItem
									style={{
										height: '24px',
										width: '24px',
										border: `solid 1px ${tokens.colorNeutralStencil1Alpha}`,
										borderRadius: '50%'
									}}
								/>
							</Skeleton>
						)}
						{!loadingPage && (
							<Button
								className={classes.goBackButton}
								appearance='transparent'
								icon={<ArrowCircleLeftRegular />}
								size='large'
								onClick={() => {
									navigate('/');
								}}
							/>
						)}
						{(loadingPage) && (
							<Skeleton>
								<SkeletonItem
									style={{
										height: 32,
										width: '240px',
										border: `solid 1px ${tokens.colorNeutralStencil1Alpha}`,
									}}
								/>
							</Skeleton>
						)}
						{(!loadingPage && titleMode === 'title') && (
							<Field
								style={{ width: '100%' }}
								validationMessage={titleValidation}
							>
								<h2
									className={classes.title}
									onClick={() => {
										setTitleMode('input');
									}}
								>
									{!title ? 'Click to add a title' : title}
								</h2>
							</Field>
						)}
						{(!loadingPage && tab === 'rule' && titleMode === 'input') && (
							<div className={classes.inputTitle}>
								<Field
									style={{ width: '100%' }}
									validationMessage={titleValidation}
								>
									<Input
										autoFocus
										placeholder='Give this automation rule a name'
										className={classes.input}
										appearance="underline"
										value={title}
										onChange={(ev) => {
											setTitle(ev.target.value);
										}}
										onBlur={() => {
											setTitleMode('title');
										}}
										onKeyDown={(ev) => {
											if (ev.key === 'Enter') {
												setTitleMode('title');
											}
										}}
									/>
								</Field>
							</div>
						)}
					</div>
					<div className={classes.headerContentRight}>
						{loadingPage && (
							<Skeleton>
								<SkeletonItem style={{ height: 32, width: '120px', border: `solid 1px ${tokens.colorNeutralStencil1Alpha}`, }} />
							</Skeleton>
						)}
						{(!loadingPage && (
							<WithTooltip
								tooltip='Before enabling, please click “Finish setup'
								showTooltip={!loadingPage && showAuthMessageBar}
							>
								<Dropdown
									className={classes.dropdown}
									disabled={!loadingPage && showAuthMessageBar}
									appearance="outline"
									placeholder='Status'
									positioning="below-end"
									value={statusOptions.find(item => item.value === status)?.label || ''}
									onOptionSelect={(_, data) => {
										setStatus(data.optionValue as string);
									}}
								>
									{statusOptions.map((statusItem) => (
										<Option key={statusItem.value} value={statusItem.value}>
											{statusItem.label}
										</Option>
									))}
								</Dropdown>
							</WithTooltip>
						))}
						{loadingPage && (
							<Skeleton>
								<SkeletonItem style={{ height: 32, width: '82px', border: `solid 1px ${tokens.colorNeutralStencil1Alpha}` }} />
							</Skeleton>
						)}
						{!loadingPage && showAuthMessageBar && (
							<Button
								appearance='primary'
								style={{ width: '140px', gap: '8px' }}
								onClick={() => {
									authorizationStepsWithModalRef.current?.open();
								}}
							>
								<CheckmarkCircle16Regular />
								Finish setup
							</Button>
						)}
						<Button
							icon={
								submitting ? <Spinner size="extra-tiny" /> : null
							}
							appearance='primary'
							onClick={onSave}
							disabled={loadingPage || submitting}
						>
							Save
						</Button>
					</div>
				</div>
			</div>
			<AuthorizationStepsWithModal ref={authorizationStepsWithModalRef} />
		</div>
	);
}

export const useStyles = makeStyles({
	root: {
		position: 'relative',
	},
	header: {
		width: '100%',
	},
	headerWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		gap: '8px'
	},
	headerContentLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		width: '100%',
		paddingLeft: '8px'
	},
	inputTitle: {
		width: '100%',
		padding: '0 12px',
		boxSizing: 'border-box'
	},
	headerContentRight: {
		display: 'flex',
		gap: '8px',
	},
	dropdown: {
		minWidth: '120px',
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':active': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		}
	},
	goBackButton: {
		padding: '4px 0px',
		minWidth: 'auto'
	},
	title: {
		display: 'inline-block',
		borderRadius: '4px',
		padding: '0 8px',
		boxSizing: 'border-box',
		width: '100%',
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase500,
		fontWeight: tokens.fontWeightSemibold,
		lineHeight: tokens.lineHeightBase500,
		':hover': {
			cursor: 'pointer',
			backgroundColor: tokens.colorSubtleBackgroundPressed,
		},
	},
	input: {
		width: '100%',
		borderBottom: 'none',
		'::after': {
			borderBottom: 'none'
		},
		'> input': {
			paddingLeft: '8px',
		},
		':has(input:focus)': {
			borderRadius: tokens.borderRadiusMedium,
			boxShadow: '0 0 0 3px rgba(0, 120, 212, .23), 0 0 0 1px rgba(0, 120, 212, 1)',
		},
	},
	authMessageBarWrapper: {
		padding: '0 8px',
		marginTop: '8px',
		marginBottom: '8px',
	},
	messageBarBody: {
		padding: '8px 0',
	}
});